import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Col, Modal, Row, Select, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Actions
import { get_depots, get_groups, get_regions, get_zones, set_area_filters } from '../../redux/actions/areasActions';
import { download_weekly_report } from '../../redux/actions/selfActions';

dayjs.locale('es');

const RandomOutputsModal = (props) => {
	const { messageApi, isModalOpen, setIsModalOpen, cycleInfo, fileId, orgFilters } = props;

	const { t } = useTranslation();
	const dispatch = useDispatch();

	const { regions, zones, depots, groups } = useSelector(state => state.areas);
	const { download_weekly_report_loading } = useSelector(state => state.self);
	const { app_config } = useSelector(state => state.apps);

	const [areaFilters, setAreaFilters] = useState({
		region: '',
		zone: '',
		depot: '',
		group: ''
	});
	const [selectedWeek, setSelectedWeek] = useState(null);
	const [weeks, setWeeks] = useState([]);

	// Generate weeks from cycle start to end
	useEffect(() => {
		if (cycleInfo?.start && cycleInfo?.end) {
			const startDate = dayjs(cycleInfo.start.$date);
			const endDate = dayjs(cycleInfo.end.$date);

			const generatedWeeks = [];
			let currentDate = startDate.clone();
			let weekNumber = 1;

			while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
				// Find the end of the current week (Sunday)
				let weekEnd;
				const dayOfWeek = currentDate.day(); // 0 = Sunday, 1 = Monday, etc.

				if (dayOfWeek === 0) {
					// If it's Sunday, this day is the end of the week
					weekEnd = currentDate.clone();
				} else {
					// Find the next Sunday
					weekEnd = currentDate.clone().add(7 - dayOfWeek, 'day');
				}

				// Make sure week end doesn't exceed cycle end
				if (weekEnd.isAfter(endDate)) {
					weekEnd = endDate.clone();
				}

				// Capitalize first letter of day name
				const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);
				const startLabel = capitalizeFirst(currentDate.format('dddd D [de] MMMM'));
				const endLabel = capitalizeFirst(weekEnd.format('dddd D [de] MMMM'));

				generatedWeeks.push({
					weekNumber,
					start: currentDate.clone(),
					end: weekEnd.clone(),
					label: `Semana ${weekNumber}: ${startLabel} - ${endLabel}`
				});

				// Move to the next Monday
				currentDate = weekEnd.add(1, 'day');
				weekNumber++;
			}

			setWeeks(generatedWeeks);
		}
	}, [cycleInfo]);

	// Load area data when filters change
	useEffect(() => {
		if (orgFilters?.organization && orgFilters?.workspace) {
			const filters = {
				organization: orgFilters.organization,
				workspace: orgFilters.workspace,
				...Object.fromEntries(
					Object.entries(areaFilters).filter(([, value]) => value !== '')
				)
			};

			dispatch(get_regions(filters));
			dispatch(get_zones(filters));
			dispatch(get_depots(filters));
			dispatch(get_groups(filters));
		}
	}, [orgFilters, areaFilters, dispatch]);

	const onChangeAreaFilters = (value, name) => {
		const newFilters = { ...areaFilters };
		const keys = Object.keys(newFilters);
		const idx = keys.indexOf(name);

		if (value === undefined) {
			// Reset all filters from the selected index onward
			keys.slice(idx).forEach((key) => {
				newFilters[key] = '';
			});
		} else {
			newFilters[name] = value;
		}

		setAreaFilters(newFilters);
		dispatch(set_area_filters(newFilters));
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		setSelectedWeek(null);
		setAreaFilters({
			region: '',
			zone: '',
			depot: '',
			group: ''
		});
	};

	const handleDownload = () => {
		if (!selectedWeek) {
			messageApi.warning('Selecciona una semana');
			return;
		}

		const week = weeks.find(w => w.weekNumber === selectedWeek);
		if (!week) return;

		const params = {
			organization: orgFilters.organization,
			workspace: orgFilters.workspace,
			cycle: cycleInfo?._id?.$oid,
			file: fileId,
			area_type: 1, // 1 represents all available areas 
			week: selectedWeek
		};

		const reportsExtra = Array.isArray(app_config)
			? app_config.reduce((acc, cfg) => ({ ...acc, ...cfg?.data?.reportsExtra }), {})
			: {};
		const extraBody = reportsExtra['weekly']?.body || {};

		const fileName = `aleatorios-${cycleInfo?.name.toLowerCase().replace(/ /g, '-')}-semana-${selectedWeek}.csv`;
		dispatch(download_weekly_report(messageApi, params, fileName, extraBody));
	};

	return (
		<Modal
			title="Descargar Aleatorios"
			open={isModalOpen}
			onCancel={handleCancel}
			maskClosable={false}
			footer={[
				<Button
					key="download"
					type="primary"
					loading={download_weekly_report_loading}
					onClick={handleDownload}
					disabled={!selectedWeek}
				>
					Descargar
				</Button>
			]}
			width={700}
			centered
		>
			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				{/* Cycle Info */}
				<div style={{ backgroundColor: '#fafafa', padding: '16px', borderRadius: '8px' }}>
					<Typography.Text strong style={{ display: 'block', marginBottom: 12 }}>
						Información del Ciclo
					</Typography.Text>
					<Row gutter={[16, 8]}>
						<Col xs={24} sm={12}>
							<Typography.Text type="secondary">Nombre:</Typography.Text>
							<Typography.Text style={{ display: 'block' }}>{cycleInfo?.name || '-'}</Typography.Text>
						</Col>
						<Col xs={24} sm={12}>
							<Typography.Text type="secondary">Descripción:</Typography.Text>
							<Typography.Text style={{ display: 'block' }}>{cycleInfo?.description || '-'}</Typography.Text>
						</Col>
						<Col xs={24} sm={12}>
							<Typography.Text type="secondary">Fecha de inicio:</Typography.Text>
							<Typography.Text style={{ display: 'block', textTransform: 'capitalize' }}>
								{cycleInfo?.start ? dayjs(cycleInfo.start.$date).format('dddd DD/MM/YYYY hh:mm:ss A') : '-'}
							</Typography.Text>
						</Col>
						<Col xs={24} sm={12}>
							<Typography.Text type="secondary">Fecha de fin:</Typography.Text>
							<Typography.Text style={{ display: 'block', textTransform: 'capitalize' }}>
								{cycleInfo?.end ? dayjs(cycleInfo.end.$date).format('dddd DD/MM/YYYY hh:mm:ss A') : '-'}
							</Typography.Text>
						</Col>
					</Row>
				</div>

				{/* Area Filters */}
				{/* <div>
					<Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
						Filtros de Área
					</Typography.Text>
					<Row gutter={[16, 16]}>
						<Col xs={24} sm={12} md={6}>
							<Typography.Text style={{ display: 'block', marginBottom: 4, textAlign: 'center' }}>
								{t('filters.region')}
							</Typography.Text>
							<Select
								style={{ width: '100%' }}
								listHeight={160}
								options={regions.regions.map(option => ({ value: option._id.$oid, label: option.name }))}
								value={regions.regions.find(option => option._id.$oid === areaFilters.region)?.name}
								onChange={(value) => onChangeAreaFilters(value, 'region')}
								allowClear
								showSearch
								filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
								placeholder="Seleccionar"
							/>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Typography.Text style={{ display: 'block', marginBottom: 4, textAlign: 'center' }}>
								{t('filters.zone')}
							</Typography.Text>
							<Select
								style={{ width: '100%' }}
								listHeight={160}
								options={zones.zones.map(option => ({ value: option._id.$oid, label: option.name }))}
								value={zones.zones.find(option => option._id.$oid === areaFilters.zone)?.name}
								onChange={(value) => onChangeAreaFilters(value, 'zone')}
								allowClear
								showSearch
								filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
								placeholder="Seleccionar"
							/>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Typography.Text style={{ display: 'block', marginBottom: 4, textAlign: 'center' }}>
								{t('filters.depot')}
							</Typography.Text>
							<Select
								style={{ width: '100%' }}
								listHeight={160}
								options={depots.depots.map(option => ({ value: option._id.$oid, label: option.name }))}
								value={depots.depots.find(option => option._id.$oid === areaFilters.depot)?.name}
								onChange={(value) => onChangeAreaFilters(value, 'depot')}
								allowClear
								showSearch
								filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
								placeholder="Seleccionar"
							/>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Typography.Text style={{ display: 'block', marginBottom: 4, textAlign: 'center' }}>
								{t('filters.group')}
							</Typography.Text>
							<Select
								style={{ width: '100%' }}
								listHeight={160}
								options={groups.groups.map(option => ({ value: option._id.$oid, label: option.name }))}
								value={groups.groups.find(option => option._id.$oid === areaFilters.group)?.name}
								onChange={(value) => onChangeAreaFilters(value, 'group')}
								allowClear
								showSearch
								filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
								placeholder="Seleccionar"
							/>
						</Col>
					</Row>
				</div> */}

				{/* Week Selector */}
				<div>
					<Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
						Seleccionar Semana
					</Typography.Text>
					<Select
						style={{ width: '100%' }}
						placeholder="Selecciona una semana"
						value={selectedWeek}
						onChange={(value) => setSelectedWeek(value)}
						options={weeks.map(week => ({
							value: week.weekNumber,
							label: week.label
						}))}
					/>
				</div>
			</Space>
		</Modal>
	);
};

RandomOutputsModal.propTypes = {
	messageApi: PropTypes.object.isRequired,
	isModalOpen: PropTypes.bool.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	cycleInfo: PropTypes.object,
	fileId: PropTypes.string.isRequired,
	orgFilters: PropTypes.object
};

export default RandomOutputsModal;
